import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';

type ContentPlanRow = Database['public']['Tables']['content_plans']['Row'];
type ContentPlanInsert = Database['public']['Tables']['content_plans']['Insert'];

export class ContentPlanService extends BaseService {
  private static instance: ContentPlanService;

  private constructor() {
    super({
      defaultTTL: 5 * 60 * 1000,
      enableCache: true,
      enableRetry: true,
    });
  }

  public static getInstance(): ContentPlanService {
    if (!ContentPlanService.instance) {
      ContentPlanService.instance = new ContentPlanService();
    }
    return ContentPlanService.instance;
  }

  async getContentPlan(): Promise<ServiceResponse<ContentPlanRow | null>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const cacheKey = `content-plan-${ownerId}`;

      return this.executeWithCache(cacheKey, async () => {
        const { data, error } = await this.client
          .from('content_plans')
          .select('*')
          .eq('user_id', ownerId)
          .maybeSingle();

        if (error) throw error;
        return data;
      });
    });
  }

  async upsertContentPlan(
    updates: Omit<ContentPlanInsert, 'user_id' | 'id'>,
  ): Promise<ServiceResponse<ContentPlanRow>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const { data, error } = await this.client
        .from('content_plans')
        .upsert(
          {
            ...updates,
            user_id: ownerId,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' },
        )
        .select()
        .single();

      if (error) throw error;

      this.clearCache(`content-plan-${ownerId}`);
      return data;
    }, 'Failed to save content plan');
  }
}

export const contentPlanService = ContentPlanService.getInstance();
